/**
 * Client game lifecycle state machine.
 *
 * Single owner of the client-side game lifecycle. All transitions are
 * idempotent — duplicate launches, duplicate ends, and StrictMode
 * mount/unmount/mount cycles are absorbed here instead of being guarded
 * against at every call site.
 *
 * Per-session states:
 *
 *   (unacquired) ──acquire──► pending ──launch──► launched ──end──► ended
 *                                │                    │               │
 *                                └──── release (refs hit 0) ──────────┘
 *                                          │
 *                                deferred teardown, cancelled
 *                                by a re-acquire in the same task
 *                                (absorbs StrictMode remounts)
 *
 * The machine is pure decision-making; all side effects (socket emits,
 * DOM mounting, Phaser teardown) are injected via LifecycleEffects so the
 * machine can be unit-tested without a DOM or a server.
 */

export type LifecycleStatus = "pending" | "launched" | "ended";

export interface AcquireOptions {
	mountPoint?: string;
	rootPoint?: string;
	onEnd?: () => void;
}

export interface LifecycleEffects {
	/** Join the session: create local session state, show loading, emit socket events. */
	join(sessionId: string, opts: AcquireOptions): void;
	/** Mount the game UI (overlay + game client). Called at most once per launch. */
	launch(sessionId: string): void | Promise<void>;
	/** Tear down the game UI and run session cleanup. */
	teardown(sessionId: string): void;
	/** Timer injection points — default to setTimeout/clearTimeout. */
	schedule(fn: () => void): unknown;
	cancel(handle: unknown): void;
}

interface Entry {
	refs: number;
	status: LifecycleStatus;
	releaseTimer: unknown | null;
}

export interface Lifecycle {
	acquire(sessionId: string, opts?: AcquireOptions): void;
	release(sessionId: string): void;
	/** Feed a server snapshot; launches if the snapshot says the session is live. */
	onSnapshot(sessionId: string, data: Record<string, unknown>): void;
	/** Feed a game:launch event. Same idempotent path as onSnapshot. */
	notifyLaunch(sessionId: string): void;
	/** End the session (server game:end or explicit). Idempotent. */
	end(sessionId: string): void;
	/** Current status, or undefined if the session was never acquired. */
	statusOf(sessionId: string): LifecycleStatus | undefined;
}

function createLifecycle(effects: LifecycleEffects): Lifecycle {
	const entries = new Map<string, Entry>();

	function tryLaunch(sessionId: string) {
		const entry = entries.get(sessionId);
		// Only sessions acquired locally may launch — snapshots are broadcast
		// wider than the session room, so unknown ids are ignored here.
		if (!entry) return;
		if (entry.status === "launched") return;
		// Set synchronously before the (possibly async) effect so a duplicate
		// launch arriving mid-effect is still a no-op.
		entry.status = "launched";
		void effects.launch(sessionId);
	}

	return {
		acquire(sessionId, opts = {}) {
			let entry = entries.get(sessionId);
			if (entry?.releaseTimer != null) {
				// A release is pending from a prior unmount (StrictMode cycle) —
				// cancel it; this acquire supersedes it.
				effects.cancel(entry.releaseTimer);
				entry.releaseTimer = null;
			}
			if (!entry) {
				entry = { refs: 0, status: "pending", releaseTimer: null };
				entries.set(sessionId, entry);
			}
			entry.refs += 1;
			// join is safe to repeat: the server re-syncs joined sockets, and a
			// snapshot with phase "launched" re-launches idempotently above.
			effects.join(sessionId, opts);
		},

		release(sessionId) {
			const entry = entries.get(sessionId);
			if (!entry) return;
			entry.refs -= 1;
			if (entry.refs > 0) return;
			// Defer teardown one task so a StrictMode remount (which runs in the
			// same task) can cancel it via acquire above.
			entry.releaseTimer = effects.schedule(() => {
				entry.releaseTimer = null;
				entries.delete(sessionId);
				if (entry.status !== "ended") {
					entry.status = "ended";
					effects.teardown(sessionId);
				}
			});
		},

		onSnapshot(sessionId, data) {
			if (data?.phase === "launched") tryLaunch(sessionId);
		},

		notifyLaunch(sessionId) {
			tryLaunch(sessionId);
		},

		end(sessionId) {
			const entry = entries.get(sessionId);
			if (entry) {
				if (entry.status === "ended") return;
				entry.status = "ended";
			}
			// Entry may legitimately be absent (direct Game.end from debug flows);
			// teardown is defensive against missing sessions.
			effects.teardown(sessionId);
		},

		statusOf(sessionId) {
			return entries.get(sessionId)?.status;
		},
	};
}

export { createLifecycle };
export default createLifecycle;
